import { useRef, useState, useLayoutEffect, useEffect, useCallback } from "react";
import {
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight,
  ArrowUpFromLine, ArrowDownToLine, RotateCcw, RotateCw,
  Hand, PlaneLanding, AlertOctagon, Plus, Minus, Crosshair, ScanEye, Aperture, GripVertical, Rocket,
} from "lucide-react";

const HOLD = (set) => ({
  onMouseDown: () => set(true),
  onMouseUp: () => set(false),
  onMouseLeave: () => set(false),
  onTouchStart: (e) => { e.preventDefault(); set(true); },
  onTouchEnd: (e) => { e.preventDefault(); set(false); },
});

function Hold({ dir, icon: Icon, label, onHold, active, disabled, testid, color = "#3B82F6", size }) {
  return (
    <button data-testid={testid} disabled={disabled} {...HOLD((on) => onHold(dir, on))}
      className={`${size || "h-11 w-11 sm:h-12 sm:w-12"} flex flex-col items-center justify-center gap-0.5 rounded-md border font-mono text-[8px] uppercase tracking-wider backdrop-blur-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
        active?.has(dir) ? "text-white" : "bg-black/45 border-white/20 text-white/80 hover:bg-black/65"
      }`}
      style={active?.has(dir) ? { background: `${color}cc`, borderColor: color } : undefined}>
      <Icon size={15} /> {label && <span className="hidden sm:block leading-none">{label}</span>}
    </button>
  );
}

function Act({ icon: Icon, label, color, onClick, disabled, testid }) {
  return (
    <button data-testid={testid} disabled={disabled} onClick={onClick}
      className="h-11 sm:h-9 min-w-[44px] px-2 flex items-center justify-center gap-1 rounded-md border font-mono text-[8px] uppercase tracking-wider backdrop-blur-md bg-black/45 hover:bg-black/65 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
      style={{ borderColor: `${color}66`, color }}>
      <Icon size={13} /> {label}
    </button>
  );
}

function Panel({ title, children, className = "", testid, handleProps }) {
  return (
    <div data-testid={testid} className={`pointer-events-auto bg-black/40 border border-white/15 rounded-lg backdrop-blur-md p-1.5 sm:p-2 ${className}`}>
      <div {...(handleProps || {})}
        className={`font-mono text-[8px] tracking-[0.2em] uppercase text-white/60 mb-1.5 flex items-center justify-center gap-1 ${handleProps ? "cursor-move touch-none select-none hover:text-white/90" : ""}`}>
        {handleProps && <GripVertical size={10} className="text-white/40" />}
        {title}
      </div>
      {children}
    </div>
  );
}

// Wrapper déplaçable des modules de pilotage. `children` est une fonction recevant
// handleProps (à passer sur le Panel = zone de glissement). Position stockée en FRACTION
// de l'espace libre du cockpit (fx,fy 0..1) => reste proportionnelle en plein écran / sortie.
export function Draggable({ pos, defaultClass = "", onChange, boundsRef, children }) {
  const startRef = useRef(null);
  const elRef = useRef(null);
  const [px, setPx] = useState(null); // { left, top } en pixels, dérivé de la fraction + bounds courants

  // Recalcule la position pixel depuis la fraction stockée et la taille actuelle du cockpit.
  const recompute = useCallback(() => {
    if (!pos) { setPx(null); return; }
    const bounds = boundsRef?.current?.getBoundingClientRect();
    const el = elRef.current;
    if (!bounds || !el) return;
    const freeW = Math.max(0, bounds.width - el.offsetWidth);
    const freeH = Math.max(0, bounds.height - el.offsetHeight);
    setPx({ left: (pos.fx ?? 0) * freeW, top: (pos.fy ?? 0) * freeH });
  }, [pos, boundsRef]);

  useLayoutEffect(() => { recompute(); }, [recompute]);
  useEffect(() => {
    const on = () => recompute();
    window.addEventListener("resize", on);
    document.addEventListener("fullscreenchange", on);
    return () => { window.removeEventListener("resize", on); document.removeEventListener("fullscreenchange", on); };
  }, [recompute]);

  const onMove = (e) => {
    const s = startRef.current; if (!s) return;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    const nx = Math.max(0, Math.min(s.bw - s.ew, s.x + (cx - s.cx)));
    const ny = Math.max(0, Math.min(s.bh - s.eh, s.y + (cy - s.cy)));
    setPx({ left: nx, top: ny });
    const freeW = Math.max(1, s.bw - s.ew), freeH = Math.max(1, s.bh - s.eh);
    onChange({ fx: nx / freeW, fy: ny / freeH });
    if (e.cancelable) e.preventDefault();
  };
  const onUp = () => {
    startRef.current = null;
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", onUp);
    window.removeEventListener("touchmove", onMove);
    window.removeEventListener("touchend", onUp);
  };
  const onDown = (e) => {
    const bounds = boundsRef?.current?.getBoundingClientRect();
    const el = elRef.current?.getBoundingClientRect();
    if (!bounds || !el) return;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    startRef.current = { cx, cy, x: el.left - bounds.left, y: el.top - bounds.top, bw: bounds.width, bh: bounds.height, ew: el.width, eh: el.height };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    if (e.cancelable) e.preventDefault();
  };
  const handleProps = { onMouseDown: onDown, onTouchStart: onDown };
  const style = px ? { left: px.left, top: px.top } : undefined;
  return (
    <div ref={elRef} className={`absolute pointer-events-none ${px ? "" : defaultClass}`} style={style}>
      {children(handleProps)}
    </div>
  );
}

// Bottom-left: drone translation joystick.
export function DroneJoystick({ onHold, active, disabled, handleProps }) {
  return (
    <Panel title="Drone · Translation" testid="drone-joystick" handleProps={handleProps}>
      <div className="grid grid-cols-3 gap-1">
        <div />
        <Hold dir="forward" icon={ChevronUp} label="Avant" onHold={onHold} active={active} disabled={disabled} testid="ctrl-forward" />
        <div />
        <Hold dir="left" icon={ChevronLeft} label="Gauche" onHold={onHold} active={active} disabled={disabled} testid="ctrl-left" />
        <Hold dir="back" icon={ChevronDown} label="Arrière" onHold={onHold} active={active} disabled={disabled} testid="ctrl-back" />
        <Hold dir="right" icon={ChevronRight} label="Droite" onHold={onHold} active={active} disabled={disabled} testid="ctrl-right" />
      </div>
    </Panel>
  );
}

// Bottom-center: altitude / yaw + drone actions (ARM+TAKEOFF/HOVER/LAND/RTL).
// Mêmes contrôles visuels en simulation et en réel ; seuls les handlers/états diffèrent (piloté par le parent).
export function DroneAux({ onHold, onAction, active, disabled, handleProps,
  realMode = false, moveDisabled, altitude = 2, onAltitude, onArmTakeoff, armDisabled = false,
  hoverDisabled, landDisabled = false, rtlDisabled = false,
  minAltitude, maxAltitude, altitudePolicyReady = false }) {
  const md = moveDisabled != null ? moveDisabled : disabled; // désactivation des axes de mouvement
  const hd = hoverDisabled != null ? hoverDisabled : md;
  return (
    <Panel title="Drone · Altitude / Yaw" testid="drone-aux" handleProps={handleProps}>
      <div className="grid grid-cols-4 gap-1">
        <Hold dir="up" icon={ArrowUpFromLine} label="Monter" onHold={onHold} active={active} disabled={md} testid="ctrl-up" />
        <Hold dir="down" icon={ArrowDownToLine} label="Desc." onHold={onHold} active={active} disabled={md} testid="ctrl-down" />
        <Hold dir="yaw_left" icon={RotateCcw} label="Yaw G" onHold={onHold} active={active} disabled={md} testid="ctrl-yaw-left" />
        <Hold dir="yaw_right" icon={RotateCw} label="Yaw D" onHold={onHold} active={active} disabled={md} testid="ctrl-yaw-right" />
      </div>
      {realMode && (
        <div className="flex items-center gap-1 mt-1" data-testid="aux-takeoff-alt-row">
          <span className="font-mono text-[8px] uppercase tracking-wider text-white/50">Alt TAKEOFF</span>
          <input data-testid="real-alt-input" type="number" min={altitudePolicyReady ? minAltitude : undefined} max={altitudePolicyReady ? maxAltitude : undefined} step={0.5} value={altitude}
            onChange={(e) => onAltitude?.(Number(e.target.value))}
            className="w-12 rounded-sm bg-black/50 border border-white/20 px-1.5 py-1 text-[10px] font-mono text-white" />
          <span data-testid="alt-policy-range" className="font-mono text-[8px] text-white/40">
            {altitudePolicyReady ? `${minAltitude}–${maxAltitude} m` : "politique en attente"}
          </span>
        </div>
      )}
      <div className={`grid ${realMode ? "grid-cols-2" : "grid-cols-3"} gap-1 mt-1`}>
        {realMode && (
          <Act icon={Rocket} label="ARM+TO" color="#22C55E" disabled={armDisabled} onClick={() => onArmTakeoff?.()} testid="ctrl-arm-takeoff" />
        )}
        <Act icon={Hand} label="Ancrer" color="#2DD4BF" disabled={hd} onClick={() => onAction("hover", "mouse")} testid="ctrl-anchor" />
        <Act icon={PlaneLanding} label="Land" color="#F59E0B" disabled={realMode ? landDisabled : disabled} onClick={() => onAction("land_sim", "mouse")} testid="ctrl-land" />
        <Act icon={AlertOctagon} label="RTL" color="#EF4444" disabled={realMode ? rtlDisabled : disabled} onClick={() => onAction("rtl_sim", "mouse")} testid="ctrl-rtl" />
      </div>
    </Panel>
  );
}

// Bottom-right: dedicated camera / gimbal joystick + zoom.
// realZoom=true : le zoom pilote la CAMÉRA réelle (Picamera2 ScalerCrop, zoom numérique 1x→10x).
export function CameraJoystick({ onHold = () => {}, onAction = () => {}, active, disabled, zoom, mode,
  realZoom = false, digitalZoom = 1, onZoomIn, onZoomOut, onZoomReset, zoomApplying = false, zoomOnly = false, handleProps }) {
  const zoomVal = realZoom ? digitalZoom : zoom;
  return (
    <Panel title="Caméra · Gimbal" testid="camera-joystick" handleProps={handleProps}>
      <div className="flex items-start gap-2">
        {!zoomOnly && (
          <div className="grid grid-cols-3 gap-1">
            <div />
            <Hold dir="cam_tilt_up" icon={ChevronUp} label="Tilt+" color="#2DD4BF" onHold={onHold} active={active} disabled={disabled} testid="cam-tilt-up" />
            <div />
            <Hold dir="cam_pan_left" icon={ChevronLeft} label="Pan G" color="#2DD4BF" onHold={onHold} active={active} disabled={disabled} testid="cam-pan-left" />
            <Hold dir="cam_tilt_down" icon={ChevronDown} label="Tilt-" color="#2DD4BF" onHold={onHold} active={active} disabled={disabled} testid="cam-tilt-down" />
            <Hold dir="cam_pan_right" icon={ChevronRight} label="Pan D" color="#2DD4BF" onHold={onHold} active={active} disabled={disabled} testid="cam-pan-right" />
          </div>
        )}
        <div className="flex flex-col gap-1">
          {realZoom ? (
            <>
              <button data-testid="cam-zoom-in" onClick={onZoomIn} disabled={zoomApplying || digitalZoom >= 10}
                className="h-8 w-9 sm:h-9 sm:w-12 flex items-center justify-center rounded-sm border border-[#2DD4BF]/40 text-[#2DD4BF] hover:bg-[#2DD4BF]/10 disabled:opacity-30"><Plus size={14} /></button>
              <div className="text-center leading-tight" data-testid="cam-zoom-readout">
                <div className="font-mono text-[11px] font-bold text-[#2DD4BF]">{(zoomVal || 1).toFixed(1)}x</div>
                <div className="font-mono text-[7px] uppercase tracking-wider text-[#525252]">num.</div>
              </div>
              <button data-testid="cam-zoom-out" onClick={onZoomOut} disabled={zoomApplying || digitalZoom <= 1}
                className="h-8 w-9 sm:h-9 sm:w-12 flex items-center justify-center rounded-sm border border-[#2DD4BF]/40 text-[#2DD4BF] hover:bg-[#2DD4BF]/10 disabled:opacity-30"><Minus size={14} /></button>
            </>
          ) : (
            <>
              <Hold dir="cam_zoom_in" icon={Plus} label="Zoom" color="#2DD4BF" onHold={onHold} active={active} disabled={disabled} testid="cam-zoom-in" size="h-8 w-9 sm:h-9 sm:w-12" />
              <div className="font-mono text-[9px] text-center text-white/80" data-testid="cam-zoom-readout">{(zoomVal || 1).toFixed(1)}x</div>
              <Hold dir="cam_zoom_out" icon={Minus} label="Zoom" color="#2DD4BF" onHold={onHold} active={active} disabled={disabled} testid="cam-zoom-out" size="h-8 w-9 sm:h-9 sm:w-12" />
            </>
          )}
        </div>
      </div>
      <div className={`grid ${zoomOnly ? "grid-cols-1" : "grid-cols-3"} gap-1 mt-1`}>
        {!zoomOnly && (
          <Act icon={Crosshair} label="Centrer" color="#2DD4BF" disabled={disabled} onClick={() => onAction("camera_center", "mouse")} testid="cam-center" />
        )}
        <Act icon={Aperture} label="Reset Z" color="#2DD4BF" disabled={realZoom ? zoomApplying : disabled}
          onClick={() => (realZoom ? onZoomReset?.() : onAction("camera_zoom_reset", "mouse"))} testid="cam-zoom-reset" />
        {!zoomOnly && (
          <Act icon={ScanEye} label={mode || "WIDE"} color="#3B82F6" disabled={disabled} onClick={() => onAction("camera_mode", "mouse")} testid="cam-mode" />
        )}
      </div>
    </Panel>
  );
}
